import React, { useMemo, useState } from "react";
import Switch from "react-switch";
import diffDays from "date-fns/differenceInDays";
import diffText from "date-fns/formatDistanceStrict";
import getYear from "date-fns/getYear";
import parse from "date-fns/parseISO";
import startOfDay from "date-fns/startOfDay";
import classnames from "classnames";
import { useHistory } from "react-router";
import { CheckCircle, Clock, Tag, Link } from "react-feather";
import { Col, Row } from "reactstrap";

import Loader from "../../common/components/Loader";
import NumberPager from "../../common/components/NumberPager";
import ProgressBar from "../../common/components/ProgressBar";
import TitleMeta from "../../common/components/TitleMeta";
import { primary, secondary } from "../../common/helpers/colors";

import { MarketingObjective as Objective } from "../../api";
import Layout from "../../components/Layout/Layout";

export interface MarketingObjectivesProps {
  objectives?: Objective[];
  year: number;
  isLoading: boolean;
  handleSelect: (id: string) => any;
}

const MarketingObjectives: React.FC<MarketingObjectivesProps> = ({
  objectives,
  year,
  isLoading,
  handleSelect,
}) => {

  const thisYear = getYear(new Date());
  const minYear = thisYear - 1;
  const maxYear = thisYear + 1;

  const history = useHistory();

  const today = startOfDay(new Date());

  const [isShowArchived, setIsShowArchived] = useState(false);

  const items = useMemo(
    () => !objectives && isLoading
      ? void 0
      : (objectives ?? []).filter(x => isShowArchived ? x.isArchived : !x.isArchived),
    [objectives, isLoading, isShowArchived]);

  const toggleMode = (swtch: boolean) => {
    setIsShowArchived(swtch);
  };

  const setYear = (year: number) => {
    history.replace(year !== thisYear
      ? `/marketing/objectives/${year}`
      : "/marketing/objectives");
  }

  return (
    <>
      <TitleMeta title="Marketing Objectives" />

      <Layout.Header>

        <Layout.Breadcrumb items={["Marketing"]} />

        <div className="d-flex align-items-start justify-content-between">
          <div className="d-flex align-items-center">
            {/* Title */}
            <Layout.Title>Objectives</Layout.Title>
            {/* Year paging */}
            <div className="ml-3">
              <NumberPager value={year} min={minYear} max={maxYear} onChange={setYear} />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <div>
              <label className="d-flex align-items-center mb-0">
                <span className="text-uppercase">Active</span>
                <div className="mx-2 d-flex">
                  <Switch
                    height={24}
                    width={48}
                    handleDiameter={20}
                    checked={isShowArchived}
                    onChange={toggleMode}
                    onColor={primary}
                    offColor={primary}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    activeBoxShadow={`0 0 2px 3px ${secondary}`} />
                </div>
                <span className="text-uppercase">Archived</span>
              </label>
            </div>
          </div>
        </div>

      </Layout.Header>

      <Layout.Body>

        <div className="position-relative" style={{ minHeight: "10em" }}>
          <Row>
            {!!items
              ? items.length !== 0
                ? (
                  <>
                    {items.map(x => {
                      const deadlineDate = x.dueDate ? parse(x.dueDate) : today;
                      const deadlineDays = diffDays(deadlineDate, today);
                      const isOverdue = deadlineDays < 0;
                      const deadlineText = isOverdue
                        ? `${diffText(today, deadlineDate)} overdue`
                        : `${diffText(deadlineDate, today)} left`;
                      return (
                        <Col key={x.id} xl={3} lg={4} md={6} sm={12} className="mb-3 d-flex flex-column">
                          <div
                            className="position-relative px-3 py-2 bg-gradient flex-grow-1 d-flex flex-column justify-content-between box-shadow"
                            style={{
                              minHeight: "4em",
                            }}>
                            <div className="d-flex flex-column-reverse">
                              {/* Name */}
                              <div className="mt-2 fs-8 fw-6 text-secondary">
                                <a href={`#${x.id}`}
                                  className="stretched-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleSelect(x.id);
                                  }}>{x.name}</a>
                              </div>
                              {/* Status */}
                              <div className="d-flex">
                                {x.isCompleted
                                  // Completed
                                  ? (
                                    <div className="text-primary d-flex align-items-center">
                                      <CheckCircle size={15} />
                                      <span className="ml-2">{x.status}</span>
                                    </div>)
                                  : x.isActive
                                    // Active
                                    ? (
                                      <div className={classnames({
                                        "d-flex align-items-center": true,
                                        "text-danger": isOverdue,
                                      })}>
                                        <Clock size={15} />
                                        <span className="ml-2">{deadlineText}</span>
                                      </div>)
                                    : (
                                      // Other
                                      <div className="text-neutral d-flex align-items-center">
                                        <Tag size={15} />
                                        <span className="ml-2">{x.status}</span>
                                      </div>)}
                                {/* Activities */}
                                {!!x.activityIds?.length && (
                                  <div className="flex-grow-1 d-flex align-items-center justify-content-end">
                                    <Link size={15} />
                                    <span className="ml-1">{x.activityIds.length}</span>
                                  </div>)}
                              </div>
                            </div>
                            {/* Progress */}
                            <div className="my-3" style={
                              !x.isActive && !x.isCompleted ? {
                                filter: "grayscale(1)",
                                opacity: .3,
                              } : void 0
                            }>
                              <ProgressBar value={x.progress ?? 0} />
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </>)
                : (
                  <Col>
                    <div className="text-center my-5">
                      No objectives found
                    </div>
                  </Col>)
              : (<></>)}
          </Row>
          <Loader show={isLoading} background="panel-light" />
        </div>

      </Layout.Body>
    </>
  );
};

export default MarketingObjectives;
